import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ListGroup } from "react-bootstrap";
import Header from "../header_h/Header";
import Footer from "../footer/Footer";
import BreadcrumbsModulePage from "../breadcrumbs/BreadcrumbsModulePage";
import DownloadPdfButton from "../download_pdf_button/DownloadPdfButton";
import { modulesData } from "./modulesData";
import { getModulesByAuthor } from "../../api/subjects";

const normalize = (v) => String(v ?? "").trim();
const lower = (v) => normalize(v).toLowerCase();

const getLegacyModulesSafe = (data, id, subject, author) => {
  if (!data) return [];

  const idStr = normalize(id);
  const idNumStr = String(Number(idStr));
  const subj = normalize(subject);
  const auth = normalize(author);

  const direct =
    data?.[idStr]?.[subj]?.[auth] ||
    data?.[idNumStr]?.[subj]?.[auth] ||
    [];

  if (Array.isArray(direct) && direct.length) return direct;

  const classKeys = Object.keys(data || {});
  const classKey =
    classKeys.find((k) => normalize(k) === idStr) ||
    classKeys.find((k) => normalize(k) === idNumStr) ||
    classKeys.find((k) => lower(k) === lower(idStr)) ||
    classKeys.find((k) => lower(k) === lower(idNumStr));

  if (!classKey) return [];

  const subjObj = data[classKey] || {};
  const subjKeys = Object.keys(subjObj || {});
  const subjKey =
    subjKeys.find((k) => normalize(k) === subj) ||
    subjKeys.find((k) => lower(k) === lower(subj));

  if (!subjKey) return [];

  const authObj = subjObj[subjKey] || {};
  const authKeys = Object.keys(authObj || {});
  const authKey =
    authKeys.find((k) => normalize(k) === auth) ||
    authKeys.find((k) => lower(k) === lower(auth));

  if (!authKey) return [];

  const arr = authObj[authKey];
  return Array.isArray(arr) ? arr : [];
};

const normalizeSlug = (m) => {
  const slug = m?.moduleSlug ?? m?.path ?? "";
  return normalize(slug);
};

const mergeModules = (legacy, mongo) => {
  const map = new Map();

  (Array.isArray(legacy) ? legacy : []).forEach((m, idx) => {
    const slug = normalizeSlug(m);
    if (!slug) return;
    map.set(slug, {
      moduleSlug: slug,
      title: normalize(m?.title),
      order: typeof m?.order === "number" ? m.order : idx,
      enabled: true,
      pdfPath: m?.pdfPath || null,
      pdfFileName: m?.pdfFileName || null,
      __source: "legacy",
    });
  });

  (Array.isArray(mongo) ? mongo : []).forEach((m, idx) => {
    const slug = normalizeSlug(m);
    if (!slug) return;

    const prev = map.get(slug);
    map.set(slug, {
      moduleSlug: slug,
      title: normalize(m?.title ?? prev?.title),
      order:
        typeof m?.order === "number"
          ? m.order
          : typeof prev?.order === "number"
            ? prev.order
            : idx,
      enabled:
        typeof m?.enabled === "boolean"
          ? m.enabled
          : typeof prev?.enabled === "boolean"
            ? prev.enabled
            : true,
      pdfPath: m?.pdfPath ?? prev?.pdfPath ?? null,
      pdfFileName: m?.pdfFileName ?? prev?.pdfFileName ?? null,
      __source: prev ? "merged" : "mongo",
    });
  });

  return Array.from(map.values())
    .filter((m) => m.enabled !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const AuthorPage = ({ userName, userRoles }) => {
  const { id, subject, author } = useParams();
  const nav = useNavigate();

  const [mongoModules, setMongoModules] = useState([]);
  const [loading, setLoading] = useState(false);

  const legacyModules = useMemo(() => {
    return getLegacyModulesSafe(modulesData, id, subject, author);
  }, [id, subject, author]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!id || !subject || !author) return;
      setLoading(true);
      try {
        const data = await getModulesByAuthor(id, subject, author);
        if (mounted) setMongoModules(Array.isArray(data) ? data : []);
      } catch (e) {
        if (mounted) setMongoModules([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [id, subject, author]);

  const modules = useMemo(() => {
    return mergeModules(legacyModules, mongoModules);
  }, [legacyModules, mongoModules]);

  return (
    <>
      <Header userName={userName} userRoles={userRoles} />
      <BreadcrumbsModulePage />
      <div className="classContainer">
        <ListGroup>
          {modules.length > 0 ? (
            modules.map((module) => (
              <div
                key={module.moduleSlug}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <ListGroup.Item
                  action
                  className="list-item"
                  variant="warning"
                  onClick={() =>
                    nav(
                      `/class/${id}/${subject}/${author}/modules/${module.moduleSlug}`
                    )
                  }
                  style={{
                    flexGrow: 1,
                    paddingRight: "10px",
                    margin: 0,
                    cursor: "pointer",
                  }}
                >
                  {module.title}
                </ListGroup.Item>

                {module.pdfPath && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "50px",
                      height: "50px",
                    }}
                  >
                    <DownloadPdfButton
                      pdfPath={module.pdfPath}
                      pdfFileName={module.pdfFileName}
                    />
                  </div>
                )}
              </div>
            ))
          ) : (
            <ListGroup.Item className="list-item">
              {loading ? "Завантаження..." : "Модулі не знайдені"}
            </ListGroup.Item>
          )}
        </ListGroup>
      </div>
      <Footer />
    </>
  );
};

export default AuthorPage;
